import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'

import DeleteModal from 'pages/parts/DeleteModal'
import HintIcon from 'pages/parts/HintIcon'
import PageTabs from 'pages/parts/PageTabs'
import { GET, POST, PUT, DELETE, httpOk } from 'helpers/api'
import { formatDate, formatMoney, formatMoneyInput } from 'helpers/helpers'

import { STATUS_CONSERVED, STATUS_DISPOSED, firstOpenDate, formatPeriod, statusClass, statusLabel } from './utils'

// Выпадашки внутри модалки — в body, иначе их обрезает прокрутка тела модалки
const SELECT_PORTAL_PROPS = {
	menuPosition: 'fixed',
	menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
	menuPlacement: 'auto',
	styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
}

// Поля, от которых зависят деньги: после первого события меняются только действиями
const FINANCIAL_FIELDS = ['pos_id', 'quantity', 'cost', 'salvage_value', 'method', 'life_months', 'commissioned_at',
	'is_opening', 'opening_period', 'opening_accumulated', 'opening_months_left', 'expense_id']

const EMPTY_FORM = {
	'name': '',
	'group_id': null,
	'inventory_number': '',
	'serial_number': '',
	'pos_id': null,
	'quantity': 1,
	'cost': '',
	'salvage_value': '',
	'method': 'linear',
	'life_months': '',
	'commissioned_at': formatDate(new Date(), 'yyyy-MM-dd'),
	'is_opening': false,
	'opening_period': null,
	'opening_accumulated': '',
	'opening_months_left': '',
	'responsible_id': null,
	'supplier_id': null,
	'terminal_id': null,
	'expense_id': null,
	'warranty_until': null,
	'note': '',
}

function money(value) {
	return value ? formatMoneyInput(value) : ''
}

function onlyNumber(value) {
	return value.replace(/[^0-9.]/g, '')
}

function AssetModal({ id, poses, references, defaultPosId, onClose, onPrintLabel }) {
	const { t } = useTranslation()

	const [assetId, setAssetId] = useState(id)
	const [tab, setTab] = useState('card')
	const [detail, setDetail] = useState(null)
	const [form, setForm] = useState({ ...EMPTY_FORM, 'pos_id': defaultPosId })
	const [initialForm, setInitialForm] = useState(null)
	const [suppliers, setSuppliers] = useState([])
	const [expenses, setExpenses] = useState([])
	const [changed, setChanged] = useState(false)
	const [modalDelete, setModalDelete] = useState(false)

	const [eventForm, setEventForm] = useState(null)
	const [maintenanceForm, setMaintenanceForm] = useState(null)

	const isNew = !assetId
	const asset = detail?.asset
	const isLocked = !!detail?.is_financial_locked
	const isDisposed = asset && asset.status === STATUS_DISPOSED

	const eventTypes = {
		'transfer': t('fa_event_transfer'),
		'modernization': t('fa_event_modernization'),
		'life_change': t('fa_event_life_change'),
		'conserve': t('fa_event_conserve'),
		'resume': t('fa_event_resume'),
		'partial_dispose': t('fa_event_partial_dispose'),
		'dispose': t('fa_event_dispose'),
		'sale': t('fa_event_sale'),
	}

	// В истории — что случилось («Перемещение»), на кнопках — что сделать («Переместить»)
	const eventTitles = {
		'transfer': t('fa_history_transfer'),
		'modernization': t('fa_history_modernization'),
		'life_change': t('fa_history_life_change'),
		'conserve': t('fa_history_conserve'),
		'resume': t('fa_history_resume'),
		'partial_dispose': t('fa_history_partial_dispose'),
		'dispose': t('fa_history_dispose'),
		'sale': t('fa_history_sale'),
	}

	async function getDetail(currentId = assetId) {
		const response = await GET('/finance/fixed-assets/' + currentId, {}, { loader: true })
		if (httpOk(response)) {
			const data = response.data
			setDetail(data)
			const nextForm = {}
			Object.keys(EMPTY_FORM).forEach(key => {
				nextForm[key] = data.asset[key] ?? EMPTY_FORM[key]
			})
			nextForm.cost = Number(data.asset.cost)
			nextForm.salvage_value = Number(data.asset.salvage_value) || ''
			nextForm.quantity = Number(data.asset.quantity)
			nextForm.opening_accumulated = Number(data.asset.opening_accumulated) || ''
			setForm(nextForm)
			setInitialForm(nextForm)
			getExpenses('', currentId, nextForm.pos_id)
		}
	}

	async function getSuppliers() {
		const response = await GET('/helper/suppliers')
		if (httpOk(response)) setSuppliers(response.data)
	}

	async function getExpenses(search = '', currentId = assetId, posId = form.pos_id) {
		var url = '/finance/fixed-assets/expenses?current_id=' + (currentId ? (detail?.asset?.expense_id || 0) : 0)
		if (posId) url += '&pos_id=' + posId
		if (search) url += '&search=' + encodeURIComponent(search)
		const response = await GET(url)
		if (httpOk(response)) setExpenses(response.data)
	}

	function setField(field, value) {
		setForm({ ...form, [field]: value })
	}

	// Группа подставляет способ и срок — человек выбирает «Транспорт», а не вспоминает «60 месяцев»
	function selectGroup(group) {
		const next = { ...form, 'group_id': group ? group.id : null }
		if (group && !isLocked) {
			next.method = group.method
			if (group.method === 'linear' && group.life_months && !form.is_opening) {
				next.life_months = group.life_months
			}
		}
		setForm(next)
	}

	function financialChanged() {
		if (!initialForm) return false
		return FINANCIAL_FIELDS.some(field => String(initialForm[field] ?? '') !== String(form[field] ?? ''))
	}

	async function save(e) {
		e.preventDefault()

		const payload = { ...form }
		if (!payload.salvage_value) payload.salvage_value = 0
		if (!payload.opening_accumulated) payload.opening_accumulated = 0

		const response = isNew
			? await POST('/finance/fixed-assets', payload, { loader: true, notification: true })
			: await PUT('/finance/fixed-assets/' + assetId, payload, { loader: true })

		if (httpOk(response)) {
			setChanged(true)
			const savedId = isNew ? response.data.id : assetId
			setAssetId(savedId)
			getDetail(savedId)
			// Новую карточку сразу показываем графиком — видно, что и с какого месяца начислится
			if (isNew) setTab('schedule')
		}
	}

	async function deleteAsset() {
		const response = await DELETE('/finance/fixed-assets/' + assetId, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			onClose(true)
		}
	}

	// --- События ---

	function openEvent(type) {
		setEventForm({
			'type': type,
			'event_date': formatDate(new Date(), 'yyyy-MM-dd'),
			'to_pos_id': null,
			'amount': '',
			'life_months': '',
			'quantity': '',
			'expense_id': null,
			'note': '',
		})
	}

	async function saveEvent(e) {
		e.preventDefault()
		const response = await POST('/finance/fixed-assets/' + assetId + '/events', eventForm, { loader: true, notification: true })
		if (httpOk(response)) {
			setEventForm(null)
			setChanged(true)
			getDetail()
		}
	}

	async function cancelEvent(eventId) {
		const response = await DELETE('/finance/fixed-assets/' + assetId + '/events/' + eventId, {}, { loader: true })
		if (httpOk(response)) {
			setChanged(true)
			getDetail()
		}
	}

	function eventDetails(event) {
		const parts = []
		if (event.type === 'transfer') parts.push((event.from_pos_name || '') + ' → ' + (event.to_pos_name || ''))
		if (event.type === 'modernization') parts.push('+' + formatMoney(event.amount))
		if (event.life_months) parts.push(t('fa_months_left') + ': ' + event.life_months)
		if (event.type === 'partial_dispose') parts.push(formatMoney(event.quantity, 3) + ' ' + t('fa_pcs'))
		if (event.type === 'sale' && Number(event.amount) > 0) parts.push(t('fa_sale_amount') + ': ' + formatMoney(event.amount))
		if (event.residual !== null && ['dispose', 'sale', 'partial_dispose'].includes(event.type)) parts.push(t('fa_written_off') + ': ' + formatMoney(event.residual))
		if (event.note) parts.push(event.note)
		return parts.join(' · ')
	}

	// --- Обслуживание ---

	function openMaintenance(item = null) {
		setMaintenanceForm(item
			? { 'id': item.id, 'title': item.title, 'interval_months': item.interval_months, 'next_due_at': item.next_due_at }
			: { 'id': null, 'title': '', 'interval_months': 6, 'next_due_at': formatDate(new Date(), 'yyyy-MM-dd') })
	}

	async function saveMaintenance(e) {
		e.preventDefault()
		const url = '/finance/fixed-assets/' + assetId + '/maintenance'
		const response = maintenanceForm.id
			? await PUT(url + '/' + maintenanceForm.id, maintenanceForm, { loader: true })
			: await POST(url, maintenanceForm, { loader: true, notification: true })
		if (httpOk(response)) {
			setMaintenanceForm(null)
			setChanged(true)
			getDetail()
		}
	}

	async function doneMaintenance(item) {
		const response = await POST('/finance/fixed-assets/' + assetId + '/maintenance/' + item.id + '/done', {}, { loader: true, notification: true })
		if (httpOk(response)) {
			setChanged(true)
			getDetail()
		}
	}

	async function deleteMaintenance(item) {
		const response = await DELETE('/finance/fixed-assets/' + assetId + '/maintenance/' + item.id, {}, { loader: true })
		if (httpOk(response)) {
			setChanged(true)
			getDetail()
		}
	}

	useEffect(() => {
		if (assetId) getDetail()
		else getExpenses('', null, defaultPosId)
		getSuppliers()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const employees = references.employees.filter(item => !form.pos_id || Number(item.pos_id) === Number(form.pos_id))
	const terminals = references.terminals.filter(item => !form.pos_id || Number(item.pos_id) === Number(form.pos_id))
	const lastEvent = detail?.events?.length ? detail.events[detail.events.length - 1] : null
	const today = formatDate(new Date(), 'yyyy-MM-dd')
	const posName = poses.find(pos => Number(pos.id) === Number(form.pos_id))?.name || ''

	const actions = []
	if (asset && !isDisposed) {
		actions.push('transfer', 'modernization', 'life_change')
		actions.push(asset.status === STATUS_CONSERVED ? 'resume' : 'conserve')
		if (Number(asset.quantity) > 1) actions.push('partial_dispose')
		actions.push('dispose', 'sale')
	}

	return (
		<>
			<Modal show={true} animation={false} centered size="xl" scrollable
				backdrop={isNew ? 'static' : true}
				onHide={() => onClose(changed)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{isNew ? t('fa_new') : (asset ? asset.inventory_number + ' · ' + asset.name : '')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ 'height': '620px' }}>
					{asset && detail &&
						<div className="row g-2 mb-3">
							{[
								{ 'label': t('fa_cost'), 'value': formatMoney(detail.summary.cost) },
								{ 'label': t('fa_accumulated'), 'value': formatMoney(detail.summary.accumulated) },
								{ 'label': t('fa_residual'), 'value': formatMoney(detail.summary.residual), 'strong': true },
								{ 'label': t('fa_monthly'), 'value': asset.method === 'none' ? t('fa_not_depreciated') : formatMoney(detail.summary.monthly) },
								{ 'label': t('fa_end_period'), 'value': detail.summary.end_period ? formatPeriod(detail.summary.end_period) : '—' },
							].map((box, index) => (
								<div className="col" key={index}>
									<div className="border rounded p-2 h-100">
										<div className="text-muted small">{box.label}</div>
										<div className={box.strong ? 'fw-bold fs-5' : 'fs-5'}>{box.value}</div>
									</div>
								</div>
							))}
							<div className={'col-auto d-flex align-items-center px-3 rounded ' + statusClass(asset.status)}>
								{statusLabel(t, asset.status)}
							</div>
						</div>
					}

					{!isNew &&
						<PageTabs
							active={tab}
							onChange={setTab}
							tabs={[
								{ 'key': 'card', 'label': t('fa_tab_card') },
								{ 'key': 'schedule', 'label': t('fa_tab_schedule') },
								{ 'key': 'events', 'label': t('fa_tab_events') + (detail?.events?.length ? ` [${detail.events.length}]` : '') },
								{ 'key': 'maintenance', 'label': t('fa_tab_maintenance') + (detail?.maintenance?.length ? ` [${detail.maintenance.length}]` : '') },
							]} />
					}

					{/* КАРТОЧКА */}
					{(isNew || tab === 'card') &&
						<form onSubmit={save} autoComplete="off" id="fixedAssetForm">
							{isLocked &&
								<div className="alert alert-info py-2">{t('fa_locked_hint')}</div>
							}
							{!isLocked && detail?.has_accruals && financialChanged() &&
								<div className="alert alert-warning py-2">{t('fa_recalculate_warning')}</div>
							}

							{/* Торговая точка не выбирается: новая карточка встаёт на точку из шапки,
								а уже заведённая переезжает только событием «Переместить» */}
							<div className="text-muted small mb-2">
								<i className="uil uil-store me-1"></i>
								{t('fa_pos_of_card', { 'pos': posName })}
								<HintIcon text={isNew ? t('fa_hint_pos_new') : t('fa_hint_pos_existing')} />
							</div>

							<div className="row">
								<div className="form-group col-md-6">
									<label>
										{t('name2')}<span className="required-mark">*</span>
										<HintIcon text={t('fa_hint_name')} />
									</label>
									<input type="text" className="form-control" value={form.name}
										placeholder={t('fa_name_placeholder')}
										onChange={(e) => setField('name', e.target.value)} />
								</div>
								<div className="form-group col-md-6">
									<label>
										{t('fa_group')}
										<HintIcon text={t('fa_hint_group')} />
									</label>
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										options={references.groups}
										value={references.groups.find(option => option.id === form.group_id) || null}
										onChange={(option) => selectGroup(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name + (option.method === 'none' ? ' · ' + t('fa_not_depreciated') : (option.life_months ? ` · ${option.life_months} ${t('fa_months_short')}` : ''))}
										getOptionValue={(option) => option.id}
									/>
								</div>
							</div>

							<div className="row">
								<div className="form-group col-md-3">
									<label>
										{t('fa_inventory_number')}
										<HintIcon text={t('fa_inventory_number_hint')} />
									</label>
									<input type="text" className="form-control" value={form.inventory_number}
										placeholder={references.next_inventory_number}
										onChange={(e) => setField('inventory_number', e.target.value)} />
								</div>
								<div className="form-group col-md-3">
									<label>
										{t('fa_serial_number')}
										<HintIcon text={t('fa_hint_serial')} />
									</label>
									<input type="text" className="form-control" value={form.serial_number || ''}
										onChange={(e) => setField('serial_number', e.target.value)} />
								</div>
								<div className="form-group col-md-3">
									<label>
										{t('quantity')}
										<HintIcon text={t('fa_quantity_hint')} />
									</label>
									<input type="text" className="form-control" value={form.quantity}
										disabled={isLocked}
										onChange={(e) => setField('quantity', onlyNumber(e.target.value))} />
								</div>
								<div className="form-group col-md-3">
									<label>
										{t('fa_commissioned_at')}<span className="required-mark">*</span>
										<HintIcon text={t('fa_hint_commissioned_at')} />
									</label>
									<div className="calendar-input">
										<DatePicker
											showMonthDropdown
											showYearDropdown
											dropdownMode="select"
											disabled={isLocked}
											className="form-control"
											dateFormat="dd.MM.yyyy"
											selected={form.commissioned_at ? new Date(form.commissioned_at) : null}
											onChange={(date) => setField('commissioned_at', date ? formatDate(date, 'yyyy-MM-dd') : null)}
											maxDate={new Date()}
										/>
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
							</div>

							<div className="row">
								<div className="form-group col-md-3">
									<label>
										{t('fa_cost')}<span className="required-mark">*</span>
										<HintIcon text={t('fa_hint_cost')} />
									</label>
									<input type="text" className="form-control" value={money(form.cost)}
										disabled={isLocked}
										onChange={(e) => setField('cost', onlyNumber(e.target.value))} />
								</div>
								<div className="form-group col-md-3">
									<label>
										{t('fa_method')}
										<HintIcon text={t('fa_hint_method')} />
									</label>
									<select className="form-select" value={form.method} disabled={isLocked}
										onChange={(e) => setField('method', e.target.value)}>
										<option value="linear">{t('fa_method_linear')}</option>
										<option value="none">{t('fa_not_depreciated')}</option>
									</select>
								</div>
								{form.method === 'linear' && !form.is_opening &&
									<div className="form-group col-md-3">
										<label>
											{t('fa_life_months')}<span className="required-mark">*</span>
											<HintIcon text={t('fa_life_months_hint')} />
										</label>
										<input type="text" className="form-control" value={form.life_months || ''}
											disabled={isLocked}
											onChange={(e) => setField('life_months', onlyNumber(e.target.value))} />
										{Number(form.life_months) > 0 && Number(form.cost) > 0 &&
											<small className="text-muted">
												≈ {formatMoney((Number(form.cost) - Number(form.salvage_value || 0)) / Number(form.life_months))} {t('fa_per_month')}
											</small>
										}
									</div>
								}
								{form.method === 'linear' &&
									<div className="form-group col-md-3">
										<label>
											{t('fa_salvage_value')}
											<HintIcon text={t('fa_salvage_value_hint')} />
										</label>
										<input type="text" className="form-control" value={money(form.salvage_value)}
											disabled={isLocked}
											onChange={(e) => setField('salvage_value', onlyNumber(e.target.value))} />
									</div>
								}
							</div>

							{/* Ввод остатков: имущество куплено до того, как начали вести учёт в Mison */}
							<div className="form-check form-switch mb-2">
								<input type="checkbox" className="form-check-input" id="faIsOpening"
									disabled={isLocked}
									checked={!!form.is_opening}
									onChange={(e) => setForm({
										...form,
										'is_opening': e.target.checked,
										'opening_period': e.target.checked ? (form.opening_period || formatDate(new Date(), 'yyyy-MM-01')) : null,
									})} />
								<label className="form-check-label" htmlFor="faIsOpening">
									{t('fa_is_opening')}
									<HintIcon text={t('fa_is_opening_hint')} />
								</label>
							</div>
							{form.is_opening &&
								<div className="row border rounded mx-0 mb-3 pt-2">
									<div className="form-group col-md-4">
										<label>
											{t('fa_opening_period')}<span className="required-mark">*</span>
											<HintIcon text={t('fa_hint_opening_period')} />
										</label>
										<div className="calendar-input">
											<DatePicker
												showMonthYearPicker
												disabled={isLocked}
												className="form-control"
												dateFormat="MM.yyyy"
												selected={form.opening_period ? new Date(form.opening_period) : null}
												onChange={(date) => setField('opening_period', date ? formatDate(date, 'yyyy-MM-01') : null)}
												maxDate={new Date()}
											/>
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
									<div className="form-group col-md-4">
										<label>
											{t('fa_opening_accumulated')}
											<HintIcon text={t('fa_hint_opening_accumulated')} />
										</label>
										<input type="text" className="form-control" value={money(form.opening_accumulated)}
											disabled={isLocked}
											onChange={(e) => setField('opening_accumulated', onlyNumber(e.target.value))} />
									</div>
									{form.method === 'linear' &&
										<div className="form-group col-md-4">
											<label>
												{t('fa_opening_months_left')}<span className="required-mark">*</span>
												<HintIcon text={t('fa_hint_opening_months_left')} />
											</label>
											<input type="text" className="form-control" value={form.opening_months_left ?? ''}
												disabled={isLocked}
												onChange={(e) => setField('opening_months_left', onlyNumber(e.target.value))} />
										</div>
									}
								</div>
							}

							<div className="row">
								<div className="form-group col-md-6">
									<label>
										{t('fa_expense')}
										<HintIcon text={t('fa_expense_hint')} />
									</label>
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										isDisabled={isLocked}
										options={expenses}
										value={expenses.find(option => Number(option.id) === Number(form.expense_id)) || (form.expense_id && detail?.expense ? detail.expense : null)}
										onChange={(option) => setField('expense_id', option ? option.id : null)}
										onInputChange={(value, meta) => { if (meta.action === 'input-change') getExpenses(value) }}
										filterOption={() => true}
										placeholder={t('fa_expense_placeholder')}
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => formatDate(option.created_at) + ' · ' + (option.purpose_name || '') + ' · ' + formatMoney(option.amount) + (option.note ? ' · ' + option.note : '')}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="form-group col-md-3">
									<label>
										{t('supplier')}
										<HintIcon text={t('fa_hint_supplier')} />
									</label>
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										options={suppliers}
										value={suppliers.find(option => Number(option.id) === Number(form.supplier_id)) || null}
										onChange={(option) => setField('supplier_id', option ? option.id : null)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="form-group col-md-3">
									<label>
										{t('fa_warranty_until')}
										<HintIcon text={t('fa_hint_warranty')} />
									</label>
									<div className="calendar-input">
										<DatePicker
											isClearable
											showMonthDropdown
											showYearDropdown
											dropdownMode="select"
											className="form-control"
											dateFormat="dd.MM.yyyy"
											selected={form.warranty_until ? new Date(form.warranty_until) : null}
											onChange={(date) => setField('warranty_until', date ? formatDate(date, 'yyyy-MM-dd') : null)}
										/>
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
							</div>

							<div className="row">
								<div className="form-group col-md-4">
									<label>
										{t('fa_responsible')}
										<HintIcon text={t('fa_hint_responsible')} />
									</label>
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										options={employees}
										value={references.employees.find(option => Number(option.id) === Number(form.responsible_id)) || null}
										onChange={(option) => setField('responsible_id', option ? option.id : null)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="form-group col-md-4">
									<label>
										{t('fa_terminal')}
										<HintIcon text={t('fa_terminal_hint')} />
									</label>
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										options={terminals}
										value={references.terminals.find(option => Number(option.id) === Number(form.terminal_id)) || null}
										onChange={(option) => setField('terminal_id', option ? option.id : null)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="form-group col-md-4">
									<label>
										{t('note')}
										<HintIcon text={t('fa_hint_note')} />
									</label>
									<input type="text" className="form-control" value={form.note || ''}
										onChange={(e) => setField('note', e.target.value)} />
								</div>
							</div>
						</form>
					}

					{/* ГРАФИК */}
					{!isNew && tab === 'schedule' && detail &&
						<div className="table-responsive">
							<table className="table table-bordered table-sm mb-0">
								<thead>
									<tr>
										<th>{t('fa_period')}</th>
										<th>{t('pos')}</th>
										<th className="text-end">{t('fa_amount')}</th>
										<th className="text-end">{t('fa_accumulated')}</th>
										<th className="text-end">{t('fa_residual')}</th>
										<th className="text-center">{t('status')}</th>
									</tr>
								</thead>
								<tbody>
									{detail.schedule.map(row => (
										<tr key={row.period} className={row.is_past ? '' : 'text-muted'}>
											<td>{formatPeriod(row.period)}</td>
											<td>{row.pos_name}</td>
											<td className="text-end">{formatMoney(row.amount, 2)}</td>
											<td className="text-end">{formatMoney(row.accumulated, 2)}</td>
											<td className="text-end">{formatMoney(row.residual, 2)}</td>
											<td className={'text-center ' + (row.is_frozen ? 'bg-success text-white' : '')}>
												{row.is_conserved ? t('fa_status_conserved') : (row.is_frozen ? t('fa_accrued_label') : t('fa_planned_label'))}
											</td>
										</tr>
									))}
									{detail.schedule.length === 0 &&
										<tr><td colSpan="6" className="text-center text-muted py-4">{t('fa_schedule_empty')}</td></tr>
									}
								</tbody>
							</table>
						</div>
					}

					{/* СОБЫТИЯ */}
					{!isNew && tab === 'events' && detail &&
						<>
							{actions.length > 0 &&
								<div className="d-flex flex-wrap gap-2 mb-3">
									{actions.map(type => (
										<button key={type} type="button"
											className={'btn btn-sm ' + (['dispose', 'sale', 'partial_dispose'].includes(type) ? 'btn-outline-danger' : 'btn-outline-primary')}
											onClick={() => openEvent(type)}>
											{eventTypes[type]}
										</button>
									))}
								</div>
							}
							<div className="table-responsive">
								<table className="table table-bordered table-sm mb-0">
									<thead>
										<tr>
											<th>{t('date')}</th>
											<th>{t('fa_event')}</th>
											<th>{t('fa_details')}</th>
											<th>{t('fa_who')}</th>
											<th style={{ 'width': '40px' }}></th>
										</tr>
									</thead>
									<tbody>
										{detail.events.map(event => (
											<tr key={event.id}>
												<td className="text-nowrap">{formatDate(event.event_date)}</td>
												<td>{eventTitles[event.type]}</td>
												<td>{eventDetails(event)}</td>
												<td className="text-muted">{event.created_by}</td>
												<td className="text-center">
													{lastEvent && lastEvent.id === event.id &&
														<i className="uil uil-times text-danger" style={{ 'cursor': 'pointer' }}
															title={t('fa_cancel_event')}
															onClick={() => cancelEvent(event.id)}></i>
													}
												</td>
											</tr>
										))}
										{detail.events.length === 0 &&
											<tr><td colSpan="5" className="text-center text-muted py-4">{t('fa_events_empty')}</td></tr>
										}
									</tbody>
								</table>
							</div>
						</>
					}

					{/* ОБСЛУЖИВАНИЕ */}
					{!isNew && tab === 'maintenance' && detail &&
						<>
							<div className="d-flex mb-3">
								<span className="text-muted vertical-center">{t('fa_maintenance_hint')}</span>
								<button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => openMaintenance()}>
									{t('add')}
								</button>
							</div>
							<div className="table-responsive">
								<table className="table table-bordered table-sm mb-0">
									<thead>
										<tr>
											<th>{t('fa_maintenance_title')}</th>
											<th className="text-end">{t('fa_interval_months')}</th>
											<th>{t('fa_next_due_at')}</th>
											<th>{t('fa_last_done_at')}</th>
											<th style={{ 'width': '150px' }}></th>
										</tr>
									</thead>
									<tbody>
										{detail.maintenance.map(item => (
											<tr key={item.id}>
												<td>{item.title}</td>
												<td className="text-end">{item.interval_months}</td>
												<td className={'text-center ' + (item.next_due_at <= today ? 'bg-danger text-white' : '')}>
													{formatDate(item.next_due_at)}
												</td>
												<td>{formatDate(item.last_done_at)}</td>
												<td className="text-nowrap text-end">
													<button type="button" className="btn btn-sm btn-success me-1" onClick={() => doneMaintenance(item)}>
														{t('fa_done')}
													</button>
													<i className="uil uil-pen me-2" style={{ 'cursor': 'pointer' }} onClick={() => openMaintenance(item)}></i>
													<i className="uil uil-times text-danger" style={{ 'cursor': 'pointer' }} onClick={() => deleteMaintenance(item)}></i>
												</td>
											</tr>
										))}
										{detail.maintenance.length === 0 &&
											<tr><td colSpan="5" className="text-center text-muted py-4">{t('fa_maintenance_empty')}</td></tr>
										}
									</tbody>
								</table>
							</div>
						</>
					}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex w-100 gap-2">
						{!isNew && !detail?.events?.length &&
							<button type="button" className="btn btn-outline-danger" onClick={() => setModalDelete(true)}>
								{t('delete')}
							</button>
						}
						{!isNew && asset &&
							<button type="button" className="btn btn-outline-primary" onClick={() => onPrintLabel({ ...asset, 'pos_name': poses.find(pos => Number(pos.id) === Number(asset.pos_id))?.name })}>
								<i className="uil uil-qrcode-scan me-1"></i>
								{t('fa_print_label')}
							</button>
						}
						<button type="button" className="btn btn-outline-warning ms-auto" style={{ 'minWidth': '140px' }} onClick={() => onClose(changed)}>
							{t('close')}
						</button>
						{(isNew || tab === 'card') &&
							<button type="submit" form="fixedAssetForm" className="btn btn-primary" style={{ 'minWidth': '140px' }}>
								{t('save')}
							</button>
						}
					</div>
				</Modal.Footer>
			</Modal>

			{/* Действие с объектом */}
			<Modal show={!!eventForm} animation={false} centered backdrop="static" onHide={() => setEventForm(null)}>
				<Modal.Header closeButton>
					<Modal.Title>{eventForm ? eventTypes[eventForm.type] : ''}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{eventForm &&
						<form onSubmit={saveEvent} autoComplete="off">
							<div className="text-muted mb-2">{t('fa_event_hint_' + eventForm.type)}</div>
							<div className="form-group">
								<label>
									{t('date')}<span className="required-mark">*</span>
									<HintIcon text={t('fa_hint_event_date')} />
								</label>
								<div className="calendar-input">
									<DatePicker
										className="form-control"
										dateFormat="dd.MM.yyyy"
										selected={eventForm.event_date ? new Date(eventForm.event_date) : null}
										onChange={(date) => setEventForm({ ...eventForm, 'event_date': date ? formatDate(date, 'yyyy-MM-dd') : null })}
										minDate={firstOpenDate(detail?.schedule)}
										maxDate={new Date()}
									/>
									<i className="uil uil-calendar-alt"></i>
								</div>
							</div>
							{eventForm.type === 'transfer' &&
								<div className="form-group">
									<label>
										{t('fa_to_pos')}<span className="required-mark">*</span>
										<HintIcon text={t('fa_hint_to_pos')} />
									</label>
									<Select
										{...SELECT_PORTAL_PROPS}
										options={poses.filter(pos => Number(pos.id) !== Number(asset?.pos_id))}
										value={poses.find(option => Number(option.id) === Number(eventForm.to_pos_id)) || null}
										onChange={(option) => setEventForm({ ...eventForm, 'to_pos_id': option.id })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
							}
							{['modernization', 'sale'].includes(eventForm.type) &&
								<div className="form-group">
									<label>
										{eventForm.type === 'sale' ? t('fa_sale_amount') : t('fa_modernization_amount')}
										{eventForm.type === 'modernization' && <span className="required-mark">*</span>}
										<HintIcon text={eventForm.type === 'sale' ? t('fa_hint_sale_amount') : t('fa_hint_modernization_amount')} />
									</label>
									<input type="text" className="form-control" value={money(eventForm.amount)}
										onChange={(e) => setEventForm({ ...eventForm, 'amount': onlyNumber(e.target.value) })} />
								</div>
							}
							{eventForm.type === 'modernization' &&
								<div className="form-group">
									<label>
										{t('fa_expense')}
										<HintIcon text={t('fa_hint_modernization_expense')} />
									</label>
									<Select
										{...SELECT_PORTAL_PROPS}
										isClearable
										options={expenses}
										value={expenses.find(option => Number(option.id) === Number(eventForm.expense_id)) || null}
										onChange={(option) => setEventForm({ ...eventForm, 'expense_id': option ? option.id : null })}
										placeholder={t('fa_expense_placeholder')}
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => formatDate(option.created_at) + ' · ' + (option.purpose_name || '') + ' · ' + formatMoney(option.amount)}
										getOptionValue={(option) => option.id}
									/>
								</div>
							}
							{['modernization', 'life_change'].includes(eventForm.type) && asset?.method === 'linear' &&
								<div className="form-group">
									<label>
										{t('fa_new_months_left')}
										{eventForm.type === 'life_change' && <span className="required-mark">*</span>}
										<HintIcon text={t('fa_hint_new_months_left')} />
									</label>
									<input type="text" className="form-control" value={eventForm.life_months}
										placeholder={detail?.summary?.months_left ? String(detail.summary.months_left) : ''}
										onChange={(e) => setEventForm({ ...eventForm, 'life_months': onlyNumber(e.target.value) })} />
								</div>
							}
							{eventForm.type === 'partial_dispose' &&
								<div className="form-group">
									<label>
										{t('fa_dispose_quantity', { 'total': formatMoney(asset?.quantity, 3) })}<span className="required-mark">*</span>
										<HintIcon text={t('fa_hint_dispose_quantity')} />
									</label>
									<input type="text" className="form-control" value={eventForm.quantity}
										onChange={(e) => setEventForm({ ...eventForm, 'quantity': onlyNumber(e.target.value) })} />
								</div>
							}
							<div className="form-group">
								<label>
									{t('note')}
									<HintIcon text={t('fa_hint_event_note')} />
								</label>
								<input type="text" className="form-control" value={eventForm.note}
									onChange={(e) => setEventForm({ ...eventForm, 'note': e.target.value })} />
							</div>
							<div className="d-flex w-100 mt-3 gap-2">
								<button type="button" className="btn btn-outline-warning w-100" onClick={() => setEventForm(null)}>{t('cancel')}</button>
								<button type="submit" className={'btn w-100 ' + (['dispose', 'sale', 'partial_dispose'].includes(eventForm.type) ? 'btn-danger' : 'btn-primary')}>
									{t('save')}
								</button>
							</div>
						</form>
					}
				</Modal.Body>
			</Modal>

			{/* Обслуживание */}
			<Modal show={!!maintenanceForm} animation={false} centered onHide={() => setMaintenanceForm(null)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('fa_tab_maintenance')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{maintenanceForm &&
						<form onSubmit={saveMaintenance} autoComplete="off">
							<div className="form-group">
								<label>
									{t('fa_maintenance_title')}<span className="required-mark">*</span>
									<HintIcon text={t('fa_hint_maintenance_title')} />
								</label>
								<input type="text" className="form-control" value={maintenanceForm.title}
									placeholder={t('fa_maintenance_placeholder')}
									onChange={(e) => setMaintenanceForm({ ...maintenanceForm, 'title': e.target.value })} />
							</div>
							<div className="row">
								<div className="form-group col-md-6">
									<label>
										{t('fa_interval_months')}<span className="required-mark">*</span>
										<HintIcon text={t('fa_hint_interval')} />
									</label>
									<input type="text" className="form-control" value={maintenanceForm.interval_months}
										onChange={(e) => setMaintenanceForm({ ...maintenanceForm, 'interval_months': onlyNumber(e.target.value) })} />
								</div>
								<div className="form-group col-md-6">
									<label>
										{t('fa_next_due_at')}<span className="required-mark">*</span>
										<HintIcon text={t('fa_hint_next_due')} />
									</label>
									<div className="calendar-input">
										<DatePicker
											className="form-control"
											dateFormat="dd.MM.yyyy"
											selected={maintenanceForm.next_due_at ? new Date(maintenanceForm.next_due_at) : null}
											onChange={(date) => setMaintenanceForm({ ...maintenanceForm, 'next_due_at': date ? formatDate(date, 'yyyy-MM-dd') : null })}
										/>
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
							</div>
							<div className="d-flex w-100 mt-3 gap-2">
								<button type="button" className="btn btn-outline-warning w-100" onClick={() => setMaintenanceForm(null)}>{t('cancel')}</button>
								<button type="submit" className="btn btn-primary w-100">{t('save')}</button>
							</div>
						</form>
					}
				</Modal.Body>
			</Modal>

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteAsset}>
			</DeleteModal>
		</>
	)
}

export default AssetModal
